import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Buildings, ChatCenteredText, Clock, CreditCard, Crown, Info, MapTrifold,
  Palette, Plugs, ShieldCheck, SquaresFour, Tag, UploadSimple, UsersThree, EnvelopeSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ServicesStatus from "../components/ServicesStatus";
import PianoSection from "./settings/PianoSection";
import GeneraliSection from "./settings/GeneraliSection";
import AziendaSection from "./settings/AziendaSection";
import ServiziSection from "./settings/ServiziSection";
import OrariSection from "./settings/OrariSection";
import ZoneTavoliSection from "./settings/ZoneTavoliSection";
import PagamentiSection from "./settings/PagamentiSection";
import BrandMediaSection from "./settings/BrandMediaSection";
import TestiNotificheSection from "./settings/TestiNotificheSection";
import TagSection from "./settings/TagSection";
import ImportazioneSection from "./settings/ImportazioneSection";
import AggregatoreSection from "./settings/AggregatoreSection";
import UtentiSection from "./settings/UtentiSection";
import PrivacySection from "./settings/PrivacySection";

const TABS = [
  { key: "generali", label: "Generali", icon: Info },
  { key: "piano", label: "Piano", icon: Crown },
  { key: "azienda", label: "Azienda", icon: Buildings },
  { key: "servizi", label: "Servizi", icon: SquaresFour },
  { key: "orari", label: "Orari e turni", icon: Clock },
  { key: "zone", label: "Zone e tavoli", icon: MapTrifold },
  { key: "pagamenti", label: "Pagamenti", icon: CreditCard },
  { key: "brand", label: "Brand & Media", icon: Palette },
  { key: "testi", label: "Testi e notifiche", icon: ChatCenteredText },
  { key: "tag", label: "Tag", icon: Tag },
  { key: "import", label: "Importazione dati", icon: UploadSimple },
  { key: "aggregatore", label: "Aggregatore", icon: EnvelopeSimple },
  { key: "integrazioni", label: "Integrazioni", icon: Plugs },
  { key: "privacy", label: "Privacy e GDPR", icon: ShieldCheck },
  { key: "utenti", label: "Utenti e ruoli", icon: UsersThree, adminOnly: true },
];

export default function Impostazioni() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "generali");
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    setError(false);
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => setError(true));
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await api.patch("/settings", patch);
      setSettings(r.data);
      toast.success("Impostazioni salvate");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const visibleTabs = TABS.filter((t) => !t.adminOnly || user?.role === "admin");
  const readOnly = !["admin", "manager"].includes(user?.role);

  return (
    <div className="flex flex-col lg:flex-row gap-6 max-w-5xl" data-testid="impostazioni-page">
      <aside className="lg:w-52 shrink-0">
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900 mb-3">Impostazioni</h1>
        <nav className="flex lg:flex-col gap-1 overflow-x-auto pb-1" data-testid="settings-tabs">
          {visibleTabs.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`settab-${key}`}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                tab === key ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"}`}>
              <Icon size={17} weight="bold" /> {label}
            </button>
          ))}
        </nav>
      </aside>

      <div className="flex-1 min-w-0">
        {error && (
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-6 text-center" data-testid="settings-error">
            <p className="text-sm text-rose-700 mb-3">Errore nel caricamento delle impostazioni</p>
            <button onClick={load} className="h-9 px-4 rounded-md bg-zinc-900 text-white text-xs font-bold">Riprova</button>
          </div>
        )}
        {!settings && !error && (
          <div className="text-sm text-zinc-400 py-8 text-center" data-testid="settings-loading">Caricamento...</div>
        )}
        {settings && tab === "generali" && <GeneraliSection settings={settings} onSave={save} saving={saving} readOnly={readOnly} />}
        {tab === "piano" && <PianoSection />}
        {settings && tab === "azienda" && <AziendaSection settings={settings} onSave={save} saving={saving} readOnly={readOnly} />}
        {settings && tab === "servizi" && <ServiziSection settings={settings} onSave={save} saving={saving} readOnly={readOnly} />}
        {settings && tab === "orari" && <OrariSection settings={settings} onSave={save} saving={saving} readOnly={readOnly} />}
        {settings && tab === "zone" && <ZoneTavoliSection />}
        {settings && tab === "pagamenti" && <PagamentiSection settings={settings} onSave={save} saving={saving} readOnly={readOnly} />}
        {settings && tab === "brand" && <BrandMediaSection settings={settings} onSave={save} saving={saving} readOnly={readOnly} />}
        {settings && tab === "testi" && <TestiNotificheSection readOnly={readOnly} />}
        {settings && tab === "tag" && <TagSection readOnly={readOnly} />}
        {settings && tab === "import" && <ImportazioneSection readOnly={readOnly} />}
        {tab === "privacy" && <PrivacySection readOnly={readOnly} />}
        {settings && tab === "aggregatore" && <AggregatoreSection readOnly={readOnly} />}
        {settings && tab === "integrazioni" && <ServicesStatus />}
        {settings && tab === "utenti" && <UtentiSection />}
      </div>
    </div>
  );
}
